
import { mixins, Options } from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'
import { maska } from 'maska'
import BaseFormDialogMixin from '@/components/common/mixins/BaseFormDialogMixin.vue'
import { IRichMenu, IRichMenuAudienceSetting, IRichMenuConditionSetting } from '@/utils/types'
import cloneDeep from 'lodash/cloneDeep'
import TestModeSelector from '@/components/common/ui/TestModeSelector.vue'
import draggable from 'vuedraggable'
import RichMenuConditionSetting from '@/components/rich-menus/selectors/RichMenuConditionSetting.vue'
import { ACTION_RICH_MENU_CONDITION_SETTING } from '@/store/actions'
import CLoading from '@/components/common/ui/CLoading.vue'

@Options({
  components: { CLoading, RichMenuConditionSetting, TestModeSelector, draggable },
  directives: { maska },
  emits: ['update:closeModal', 'onUpdateSettings'],
})
export default class ConditionSettingModal extends mixins(BaseFormDialogMixin) {
  @Prop()
  settingData!: IRichMenuConditionSetting

  @Prop()
  activeRichMenus!: IRichMenu[]

  @Prop()
  testRichMenus!: IRichMenu[]

  @Prop()
  appId!: string

  loading = false
  isTestMode = false
  expandedDefault = true
  expandedAudience = true
  defaultRichMenu: IRichMenu = {}
  priorityRichMenus: IRichMenuAudienceSetting[] = []
  selectedRichMenuSetting: IRichMenuAudienceSetting = {}
  form: IRichMenuConditionSetting = {
    _id: '',
    app_id: '',
    display_setting: false,
    prod_detail: {
      default_rich_menu_id: '',
      priority_rich_menu: [
        {
          _id: '',
          name: '',
          image_url: '',
          audience: {
            audience_or: [],
            audience_not: [],
          },
        },
      ],
    },
    test_detail: {
      default_rich_menu_id: '',
      priority_rich_menu: [
        {
          _id: '',
          name: '',
          image_url: '',
          audience: {
            audience_or: [],
            audience_not: [],
          },
        },
      ],
    },
  }

  onSelectRichMenu(richMenu: IRichMenuAudienceSetting) {
    this.selectedRichMenuSetting = richMenu
  }

  onSetRichMenuDefault(id) {
    if (this.isTestMode && this.form.test_detail) {
      this.form.test_detail.default_rich_menu_id = id
      this.defaultRichMenu =
        this.testRichMenus.find((item) => item._id === this.form.test_detail?.default_rich_menu_id) ?? {}
    } else if (!this.isTestMode && this.form.prod_detail) {
      this.form.prod_detail.default_rich_menu_id = id
      this.defaultRichMenu =
        this.activeRichMenus.find((item) => item._id === this.form.prod_detail?.default_rich_menu_id) ?? {}
    }
    this.handleRichMenus()
    this.onSelectDefaultRichMenu()
  }

  onSelectDefaultRichMenu() {
    this.selectedRichMenuSetting = {
      _id: this.defaultRichMenu._id,
      chat_bar_text: this.isTestMode
        ? this.defaultRichMenu.test_detail?.chat_bar_text
        : this.defaultRichMenu.prod_detail?.chat_bar_text,
      name: this.defaultRichMenu.name,
      image_url: this.isTestMode
        ? this.defaultRichMenu.test_detail?.rich_menu_content?.[0]?.image_url
        : this.defaultRichMenu.prod_detail?.rich_menu_content?.[0]?.image_url,
      audience: {
        audience_or: [],
        audience_not: [],
      },
    }
  }

  onUpdateFormData() {
    if (this.isTestMode && this.form.test_detail) {
      this.form.test_detail.priority_rich_menu = this.priorityRichMenus
    } else if (!this.isTestMode && this.form.prod_detail) {
      this.form.prod_detail.priority_rich_menu = this.priorityRichMenus
    }
  }

  async onSubmit() {
    this.loading = true
    this.onUpdateFormData()
    const { dispatch } = this.$store
    let item = null
    if (this.isNew) {
      this.form.test_detail = this.form.prod_detail
      item = await dispatch(ACTION_RICH_MENU_CONDITION_SETTING.ADD, {
        ...this.form,
      })
    } else {
      item = await dispatch(ACTION_RICH_MENU_CONDITION_SETTING.UPDATE, {
        ...this.form,
      })
    }
    if (item) {
      this.$q.notify({
        message: this.$t('messages.saved'),
        color: 'positive',
      })
      this.$emit('onUpdateSettings', item)
    }
    this.loading = false
    this.visible = false
  }

  @Watch('isTestMode')
  handleRichMenus() {
    if (this.isTestMode) {
      this.defaultRichMenu =
        this.testRichMenus.find((item) => item._id === this.form.test_detail?.default_rich_menu_id) ?? {}

      this.priorityRichMenus = this.mergeRichMenuWithAudience(
        this.testRichMenus,
        this.form.test_detail?.priority_rich_menu ?? [],
        true,
        this.defaultRichMenu._id ?? ''
      )
    } else {
      this.defaultRichMenu =
        this.activeRichMenus.find((item) => item._id === this.form.prod_detail?.default_rich_menu_id) ?? {}

      this.priorityRichMenus = this.mergeRichMenuWithAudience(
        this.activeRichMenus,
        this.form.prod_detail?.priority_rich_menu ?? [],
        false,
        this.defaultRichMenu._id ?? ''
      )
    }
    if (this.priorityRichMenus.length > 0) {
      this.selectedRichMenuSetting = this.priorityRichMenus[0]
    } else if (this.defaultRichMenu._id) {
      this.onSelectDefaultRichMenu()
    } else {
      this.selectedRichMenuSetting = {
        _id: '',
        name: '',
        image_url: '',
        chat_bar_text: '',
        audience: {
          audience_or: [],
          audience_not: [],
        },
      }
    }
  }

  mergeRichMenuWithAudience(
    menus: IRichMenu[],
    audienceSettings: IRichMenuAudienceSetting[],
    isTestMode: boolean,
    defaultMenuId?: string
  ): IRichMenuAudienceSetting[] {
    const audienceMap = new Map(audienceSettings.filter((item) => item._id).map((item) => [item._id, item]))

    const filteredMenus = menus.filter((menu) => menu._id !== defaultMenuId)

    return filteredMenus.map((menu) => {
      const setting = audienceMap.get(menu._id || '')
      return {
        _id: menu._id,
        name: menu.name,
        chat_bar_text: isTestMode ? menu.test_detail?.chat_bar_text : menu.prod_detail?.chat_bar_text,
        image_url: isTestMode
          ? menu.test_detail?.rich_menu_content?.[0]?.image_url
          : menu.prod_detail?.rich_menu_content?.[0]?.image_url,
        audience: {
          audience_or: setting?.audience?.audience_or ?? [],
          audience_not: setting?.audience?.audience_not ?? [],
        },
      }
    })
  }

  async created() {
    this.form = { ...this.form, ...cloneDeep(this.settingData) }
    this.parentForm = this.form as any
    this.initForm = cloneDeep(this.parentForm)
    this.handleRichMenus()
    if (this.priorityRichMenus.length > 0) {
      this.selectedRichMenuSetting = this.priorityRichMenus[0]
    } else if (this.defaultRichMenu._id) {
      this.onSelectDefaultRichMenu()
    } else {
      this.selectedRichMenuSetting = {
        _id: '',
        name: '',
        image_url: '',
        chat_bar_text: '',
        audience: {
          audience_or: [],
          audience_not: [],
        },
      }
    }
  }
}
